import { gql } from '@apollo/client';
import { FULL_CHANNEL } from './pages/queries.tsx';
import { BASE_DOCUMENT, BASE_FIELD, PRIMITIVE_FIELD, SELECT_FIELD_FRAGMENT } from '../../document/queries.ts';

const FULL_PROPERTY_BILL = gql`
    ${SELECT_FIELD_FRAGMENT}
    
    fragment FullPropertyBill on DbImmoPropertyBill {
        ${BASE_DOCUMENT}

        kreditor {
            name
            customerNumber
            customerType
            misc
            contactPerson
            ${BASE_FIELD}
            address {
                addressId
                name
                postcode
                city
                country
                phone
                email
                companyName
                streetAndNr
                misc
                bbox
                pageIndex
                predictionConfidence
            }
            internalNote
        }
        kreditorName {
            ${PRIMITIVE_FIELD}
        }
        kreditorPlz {
            ${PRIMITIVE_FIELD}
        }
        kreditorStadt {
            ${PRIMITIVE_FIELD}
        }
        referenzVorgaenger {
            ${PRIMITIVE_FIELD}
        }
        bescheidDatum {
            ${PRIMITIVE_FIELD}
        }

        bescheidEndeDatum {
            ${PRIMITIVE_FIELD}
        }
        primaryAktenzeichen {
            ${PRIMITIVE_FIELD}
        }
        secondaryAktenzeichen {
            ${PRIMITIVE_FIELD}
        }
        sepaGlaeubigerId {
            ${PRIMITIVE_FIELD}
        }

        sepaMandatsReferenz {
            ${PRIMITIVE_FIELD}
        }
        grundsteuerArt {
            ...FullSelectField
        }
        objekte {
            ${BASE_FIELD}
            objektNummer {
                ${PRIMITIVE_FIELD}
            }
            objektName {
                ${PRIMITIVE_FIELD}
            }
        }
        spitzabrechnungen {
            ${BASE_FIELD}
            dueDate {
                ${PRIMITIVE_FIELD}
            }
            amountNet {
                ${PRIMITIVE_FIELD}
            }
            amountGross {
                ${PRIMITIVE_FIELD}
            }
            taxCode {
                ...FullSelectField
            }
            kostenart {
                ...FullSelectField
            }
            kostenartText {
                ${PRIMITIVE_FIELD}
            }
            verrechnung {
                ...FullSelectField
            }
        }
        abschlagszahlungen {
            ${BASE_FIELD}
            firstPaymentDate {
                ${PRIMITIVE_FIELD}
            }
            interval {
                ...FullSelectField
            }
            intervalText {
                ${PRIMITIVE_FIELD}
            }
            amount {
                ${PRIMITIVE_FIELD}
            }
            taxCode {
                ...FullSelectField
            }
            kostenart {
                ...FullSelectField
            }
            kostenartText {
                ${PRIMITIVE_FIELD}
            }
        }
    }
`;

const FULL_RECORD = gql`
    ${FULL_PROPERTY_BILL}
    ${FULL_CHANNEL}
    fragment FullRecord on PropertyBillProcessingRecord {
        id
        createdAt
        deletedAt
        deletedBy {
            email
            firstName
            lastName
        }
        deletedReason
        emailCount
        sender
        subject

        status

        canFinishHeaderAssistance
        canFinishAbschlagszahlungenAssistance
        canFinishSpitzabrechnungenAssistance
        canFinishObjekteAssistance
        canFinishAssistance

        isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)
        isAbschlagszahlungenCorrect: canFinishAbschlagszahlungenAssistance(ignoreThreshold: false)
        isSpitzabrechnungenCorrect: canFinishSpitzabrechnungenAssistance(ignoreThreshold: false)
        isObjekteCorrect: canFinishObjekteAssistance(ignoreThreshold: false)

        lockedBy {
            email
        }

        putOnHoldReason
        putOnHoldBy {
            email
            firstName
            lastName
        }

        assistedBy {
            email
            firstName
            lastName
        }

        assistedAt

        createdBy {
            email
            firstName
            lastName
        }

        assignedTo {
            id
            firstName
            lastName
            email
        }

        createdSource

        stepRun {
            id
            stepName
            manualOnly
            executionStatus
            error
            context
        }

        propertybillprocessingsteprunSet {
            id
            createdAt
            updatedAt
            stepName
            manualOnly
            executionStatus
            context
            error
        }

        channel {
            ...FullChannel
        }

        isDiscarded
        isTestDocument

        discardRecord {
            reason
            user {
                id
                firstName
                lastName
            }
            createdAt
            updatedAt
        }

        deliveryRecord {
            deliveryMethod
            deliveryStatus
            externalReference
            externalStatus
            externalMessage
        }

        sourceFile {
            ... on Email {
                sender {
                    email
                    name
                }
                receivers {
                    email
                    name
                }
                subject
                messageId
                language
                receivedAt

                text
                html
                attachments {
                    cid
                    contentDisposition
                    name
                    type
                    url
                    sourcePartId
                }

                senderClientId
                prediction
                predictionConfidence
                sourceUrl

                predictionConfidences
            }

            ... on File {
                name
                type
                url
            }
        }

        sourceParts {
            id
            fileName
            mimeType
            partType
            fileUrl
            classificationLabel
            classificationConfidence
            recordId
            classificationCorrectedBy
            classificationCorrectedAt
        }

        inputFile {
            ... on File {
                name
                type
                sourceType
            }
        }

        outputFile {
            ... on Export {
                files {
                    name
                    url
                }
            }
        }

        propertyBillUnsaved {
            ...FullPropertyBill
        }

        labelingStatus

        masterdataVersionsExtracted
    }
`;

export const GET_OVERVIEW_DATA = gql`
    query {
        propertyBillProcessingChannels {
            id
            name
        }
    }
`;

export const GET_CHANNELS_OVERVIEW_DATA = gql`
    query {
        propertyBillProcessingChannels {
            id
            name
            emailAddress
            team {
                id
                users {
                    email
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_TABLE_OVERVIEW_DATA = gql`
    query ($filters: [TableFilter], $orderBy: String, $offset: Int, $first: Int) {
        filteredPropertyBillProcessingRecordsList(
            filters: $filters
            orderBy: $orderBy
            offset: $offset
            limit: $first
        ) {
            totalCount
            records {
                id
                sender
                status
                createdAt
                finishedAt
                assistedAt
                assistedBy {
                    id
                    firstName
                    lastName
                    email
                }
                createdBy {
                    email
                    firstName
                    lastName
                }
                assignedTo {
                    id
                    firstName
                    lastName
                    email
                }
                createdSource
                deletedAt
                deletedBy {
                    id
                    firstName
                    lastName
                    email
                }
                deletedReason
                lockedBy {
                    email
                }
                stepRun {
                    id
                    stepName
                    manualOnly
                    executionStatus
                    error
                    context
                }
                channel {
                    id
                    name
                }
                isDiscarded
                deliveryRecord {
                    deliveryMethod
                    deliveryStatus
                    externalReference
                    externalStatus
                    externalMessage
                }
                discardRecord {
                    reason
                    user {
                        id
                        firstName
                        lastName
                        email
                    }
                    createdAt
                    updatedAt
                }
                isTestDocument
                canProcessAutomatically
                title
                subtitle
                attachmentsCount
                sourcePart {
                    sender {
                        email
                        name
                    }
                }
                emailCount
                putOnHoldReason
                putOnHoldBy {
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_ASSISTANCE_OVERVIEW_DATA = gql`
    query ($recordId: UUID!, $filters: [TableFilter]) {
        propertyBillProcessingRecordAssistancePagination(recordId: $recordId, filters: $filters) {
            filteredCount
            position
            previousRecordId
            nextRecordId
        }
    }
`;

export const GET_ASSISTANCE_DATA = gql`
    ${FULL_RECORD}
    ${FULL_PROPERTY_BILL}
    query ($id: UUID!) {
        propertyBillProcessingRecord(id: $id) {
            ...FullRecord

            propertyBillExtracted {
                ...FullPropertyBill
            }

            dusDocument {
                pagesCount
                processedFileUrl
            }

            dusEmail {
                pagesCount
                processedFileUrl
            }
        }
    }
`;

export const UPDATE_PROCESSING_RECORD = gql`
    mutation (
        $recordId: UUID!
        $fieldName: String
        $itemIndex: Int
        $action: String!
        $payload: GenericScalar
        $itemType: String
    ) {
        propertyBillUpdate(
            recordId: $recordId
            fieldName: $fieldName
            itemIndex: $itemIndex
            action: $action
            payload: $payload
            itemType: $itemType
        ) {
            record {
                canFinishHeaderAssistance
                canFinishAbschlagszahlungenAssistance
                canFinishSpitzabrechnungenAssistance
                canFinishObjekteAssistance
                canFinishAssistance

                isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)
                isAbschlagszahlungenCorrect: canFinishAbschlagszahlungenAssistance(ignoreThreshold: false)
                isSpitzabrechnungenCorrect: canFinishSpitzabrechnungenAssistance(ignoreThreshold: false)
                isObjekteCorrect: canFinishObjekteAssistance(ignoreThreshold: false)
                stepRun {
                    id
                    executionStatus
                    manualOnly
                    stepName
                }
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const RESELECT_PROCESSING_RECORD = gql`
    mutation (
        $recordId: UUID!
        $fieldName: String
        $bbox: [[Float]]!
        $pageIndex: Int!
        $itemIndex: Int
        $itemType: String
        $isEmailOcr: Boolean
    ) {
        propertyBillReselect(
            recordId: $recordId
            fieldName: $fieldName
            bbox: $bbox
            pageIndex: $pageIndex
            itemIndex: $itemIndex
            itemType: $itemType
            isEmailOcr: $isEmailOcr
        ) {
            record {
                canFinishHeaderAssistance
                canFinishAbschlagszahlungenAssistance
                canFinishSpitzabrechnungenAssistance
                canFinishObjekteAssistance
                canFinishAssistance

                isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)
                isAbschlagszahlungenCorrect: canFinishAbschlagszahlungenAssistance(ignoreThreshold: false)
                isSpitzabrechnungenCorrect: canFinishSpitzabrechnungenAssistance(ignoreThreshold: false)
                isObjekteCorrect: canFinishObjekteAssistance(ignoreThreshold: false)
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const PROPERTY_BILL_EXTRACT_TEXT = gql`
    mutation PropertyBillExtractText(
        $recordId: UUID!
        $pageIndex: Int!
        $bbox: [[Float]]!
        $isEmailOcr: Boolean = false
    ) {
        propertyBillExtractText(recordId: $recordId, pageIndex: $pageIndex, bbox: $bbox, isEmailOcr: $isEmailOcr) {
            bbox
            text
        }
    }
`;

export const RETRY_STEP = gql`
    mutation ($recordId: UUID!, $stepRunId: UUID!) {
        propertyBillRetryStep(recordId: $recordId, stepRunId: $stepRunId) {
            success
        }
    }
`;

export const UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $batchId: String, $isTestingDocument: Boolean!) {
        propertyBillUploadFile(
            input: { channelId: $channelId, files: $files, batchId: $batchId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const RE_UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $recordId: String!, $isTestingDocument: Boolean!) {
        propertyBillUploadFile(
            input: { channelId: $channelId, files: $files, recordId: $recordId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const SEND_TO_LABELING = gql`
    mutation ($recordId: UUID!) {
        propertyBillSendToLabeling(recordId: $recordId) {
            success
        }
    }
`;

export const GET_NEXT_ASSISTANCE_RECORD = gql`
    query ($filters: [TableFilter], $channelId: UUID, $orderBy: String) {
        nextPropertyBillProcessingRecord(filters: $filters, channelId: $channelId, orderBy: $orderBy) {
            id
        }
    }
`;

export const CREATE_CHANNEL = gql`
    mutation ($name: String!, $fromExistingChannelId: UUID) {
        createPropertyBillProcessingChannel(name: $name, fromExistingChannelId: $fromExistingChannelId) {
            channel {
                id
            }
            success
        }
    }
`;

export const GET_ALL_CHANNELS = gql`
    ${FULL_CHANNEL}
    query {
        propertyBillProcessingChannels {
            ...FullChannel
        }
    }
`;

export const GET_RELATED_DOCUMENT = gql`
    query ($id: UUID!) {
        dbImmoProcessingRecord(id: $id) {
            id
            subject
            createdAt
            finishedAt
            assistedAt
            assistedBy {
                id
                firstName
                lastName
                email
            }
            status
        }
    }
`;
